/* eslint-disable @typescript-eslint/no-explicit-any */
import type { MuiPickersAdapter } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';
import isNil from 'lodash/isNil';
import { Frequency, RRule } from 'rrule';
import type * as Yup from 'yup';
import { create } from 'zustand';

import { type EndDetails, EndType } from '../components/End/End.types';
import { type AllRepeatDetails, MonthBy, Weekday, YearlyBy } from '../components/Repeat/Repeat.types';
import { buildRRuleString } from '../utils/buildRRuleString/buildRRuleString';
import getValidationSchema from '../validation/validationSchema';

interface BuilderState<TDate extends Dayjs> {
  repeatDetails: AllRepeatDetails;
  frequency: Frequency;
  startDate: TDate | null;
  dateAdapter?: MuiPickersAdapter;
  validationErrors: Record<string, string>;
  endDetails: EndDetails<TDate>;
  RRuleString?: string;
  radioValue: MonthBy | YearlyBy | null;
  minEndDate?: TDate;
}

interface BuilderActions<TDate extends Dayjs> {
  validationErrors: Record<string, string>;
  setAdapter: (dateAdapter: MuiPickersAdapter) => void;
  setFrequency: (frequency: Frequency) => void;
  setRepeatDetails: (details: AllRepeatDetails) => void;
  validateForm: () => Promise<boolean>;
  setEndDetails: (details: EndDetails<TDate>) => void;
  setStartDate: (startDate: TDate | null) => void;
  buildRRuleString: () => void;
  onChange?: (rruleString: string) => void;
  setOnChange: (onChange: (rruleString: string) => void) => void;
  setStoreFromRRuleString: (rruleString: string) => void;
  setRadioValue: (radioValue: MonthBy | YearlyBy | null) => void;
}

export const baseRepeatDetails: AllRepeatDetails = {
  interval: 1,
  bySetPos: [],
  byMonth: [],
  byMonthDay: [],
  byDay: [],
};

const asArray = <T,>(value?: T | T[] | null): T[] => {
  if (isNil(value)) return [];
  return Array.isArray(value) ? value : [value];
};

const initialState: BuilderState<any> = {
  repeatDetails: baseRepeatDetails,
  frequency: Frequency.WEEKLY,
  startDate: null,
  validationErrors: {},
  endDetails: { endingType: EndType.NEVER, endDate: null, occurrences: null },
  radioValue: null,
};

const useBuilderStore = create<BuilderState<any> & BuilderActions<any>>((set, get) => ({
  ...initialState,
  validationErrors: {},
  setRadioValue: (radioValue) => set({ radioValue }),
  setFrequency: (frequency) => {
    set({ frequency });
    // clear repeat details when changing frequency
    set({ repeatDetails: initialState.repeatDetails });
    // clear validation errors
    set({ validationErrors: {} });
    // rebuild the rrule string
    get().buildRRuleString();
  },
  setStartDate: (startDate) => {
    const { endDate } = get().endDetails;
    const { dateAdapter } = get();
    // without a date adapter, we can't do anything
    if (!dateAdapter) {
      return;
    }

    // set the min end date
    if (startDate) {
      const minEndDate = dateAdapter.addDays(startDate, 1);
      set({ minEndDate });
    }

    // Use the dateAdapter methods to compare and manipulate dates
    if (endDate && startDate && (dateAdapter.isEqual(startDate, endDate) || dateAdapter.isBefore(startDate, endDate))) {
      // Adjust the end date to ensure it is not before the start date
      const adjustedEndDate = dateAdapter.addDays(startDate, 1);
      set({
        endDetails: { ...get().endDetails, endDate: adjustedEndDate },
      });
    }
    // set the value
    set({ startDate });
    // clear validation errors
    set({ validationErrors: {} });
    // rebuild the rrule string
    get().buildRRuleString();
  },
  setEndDetails: (details) => {
    set({ endDetails: details });

    // rebuild the rrule string
    get().buildRRuleString();
  },
  setRepeatDetails: (details) => {
    set({ repeatDetails: details });
    // rebuild the rrule string
    get().buildRRuleString();
  },
  validateForm: async () => {
    const { repeatDetails, frequency } = get();
    if (!frequency) {
      set({ validationErrors: { frequency: 'Frequency is required' } });
      return false;
    }
    const validationSchema = getValidationSchema(frequency);
    try {
      await validationSchema.validate({ ...repeatDetails, frequency }, { abortEarly: false });
      set({ validationErrors: {} });
      return true;
    } catch (error) {
      const errors = (error as Yup.ValidationError).inner.reduce(
        (acc: Record<string, string>, err) => ({
          ...acc,
          [err.path!]: err.message,
        }),
        {},
      );
      set({ validationErrors: errors });
      return false;
    }
  },
  buildRRuleString: () => {
    const { repeatDetails, frequency, startDate, endDetails, dateAdapter } = get();

    if (!dateAdapter) {
      return;
    }

    const output = buildRRuleString({
      frequency,
      startDate,
      repeatDetails,
      endDetails,
      dateAdapter,
    });

    set({ RRuleString: output });

    // if there is an onChange function, call it with the output
    const { onChange } = get();
    if (onChange) onChange(output);
  },
  setOnChange: (onChange) => set({ onChange }),
  setStoreFromRRuleString: (rruleString) => {
    const parsedObj = RRule.parseString(rruleString);
    const { setFrequency, setStartDate, setEndDetails, setRepeatDetails, dateAdapter } = get();

    if (!isNil(parsedObj.freq)) {
      setFrequency(parsedObj.freq);

      const isYearlyBySetPos = parsedObj.freq === Frequency.YEARLY && (parsedObj.byweekday || parsedObj.bysetpos);
      const isYearlyByMonth = parsedObj.freq === Frequency.YEARLY && (parsedObj.bymonth || parsedObj.bymonthday);
      const isMonthlyByMonthDay = parsedObj.freq === Frequency.MONTHLY && parsedObj.bymonthday;
      const isMonthlyBySetPos = parsedObj.freq === Frequency.MONTHLY && (parsedObj.bysetpos || parsedObj.byweekday);

      if (isYearlyBySetPos) set({ radioValue: YearlyBy.BYSETPOS });
      else if (isYearlyByMonth) set({ radioValue: YearlyBy.BYMONTH });
      else if (isMonthlyByMonthDay) set({ radioValue: MonthBy.BYMONTHDAY });
      else if (isMonthlyBySetPos) set({ radioValue: MonthBy.BYSETPOS });
    }

    if (parsedObj.dtstart) {
      setStartDate(dateAdapter?.date(parsedObj.dtstart.toISOString()) ?? null);
    }

    if (parsedObj.until) {
      setEndDetails({
        endingType: EndType.ON,
        endDate: dateAdapter?.date(parsedObj.until.toISOString()) ?? null,
        occurrences: null,
      });
    } else if (parsedObj.count) {
      setEndDetails({
        endingType: EndType.AFTER,
        occurrences: parsedObj.count,
        endDate: null,
      });
    }

    setRepeatDetails({
      interval: parsedObj.interval ?? null,
      byDay: asArray(parsedObj.byweekday).map((day) => {
        if (typeof day === 'number') {
          return day as unknown as Weekday;
        }
        return Weekday[day as keyof typeof Weekday];
      }),
      byMonthDay: asArray(parsedObj.bymonthday),
      byMonth: asArray(parsedObj.bymonth),
      bySetPos: asArray(parsedObj.bysetpos),
    });
  },
  setAdapter: (dateAdapter) => {
    set({ dateAdapter });
  },
}));

export default useBuilderStore;
